import React, { useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { z } from "zod";
import { toast } from "sonner";
import { baseURL } from "../../../Utils/constants/constant";
import { PostRequest } from "../../../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";

/* ── Validation schema ── */

const composeSchema = z.object({
  to: z
    .string()
    .trim()
    .min(1, "Recipient is required")
    .refine(
      (val) =>
        val
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
          .every((v) => /^[\w.%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(v)),
      "One or more recipient emails are invalid"
    ),
  cc: z
    .string()
    .optional()
    .or(z.literal("")) 
    .refine(
      (val) =>
        !val || 
        val
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
          .every((v) => /^[\w.%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(v)),
      "One or more CC emails are invalid"
    ),
  bcc: z
    .string()
    .optional()
    .or(z.literal("")) 
    .refine(
      (val) =>
        !val || 
        val
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
          .every((v) => /^[\w.%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(v)),
      "One or more BCC emails are invalid"
    ),
  subject: z.string().min(1, "Subject is required"),
  body: z.string().min(1, "Message body is required"),
});

/* ── toBase64 helper ── */
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function ComposeOffcanvas({
  open,
  onClose,
  mail,
  type = "new",
  recipients = [],
  originalMail,
}) {
  const EMPTY_FORM = {
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
    attachments: [],
  };
  const [customer, setCustomer] = useLocalStorageState("customer");
  const [composeData, setComposeData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const fileInputRef = useRef(null);

  const accessToken = sessionStorage.getItem("email_access_token");

  /* ── normalise subject prefix ── */
  const normalizeSubject = (subject = "") => {
    let s = subject.replace(/^(Re:\s*)+/i, "");
    s = s.replace(/^(PUG_CRM\s*)+/i, "");
    return s.trim();
  };

  /* ── Auto-fill when opening for reply / forward ── */
  useEffect(() => {
    if (!open) {
      return;
    }

    if (!mail && type === "new") {
      resetForm();
      return;
    }

    if (!mail) return;

    const baseSubject = normalizeSubject(mail.subject || "");
    const originalBody = originalMail?.body?.content || originalMail?.bodyPreview || "";
    if (type === "reply") {
      const replyTo = mail?.toRecipients[0]?.emailAddress?.address;
      setShowCc(false);
      setShowBcc(false);
      setComposeData({
        to: replyTo,
        cc: "",
        bcc: "",
        subject: `Re: PUG_CRM ${baseSubject}`,
        body: `
          <br/><br/>
          <hr/>
          <b>From:</b> ${mail.from?.emailAddress?.address || ""}<br/>
          <b>Sent:</b> ${new Date(mail.sentDateTime).toLocaleString()}<br/>
          <b>Subject:</b> ${mail.subject || ""}<br/>
          <br/>
          ${originalBody}
        `,
        attachments: [],
      });
    } else if (type === "replyAll") {
      const me = (sessionStorage.getItem("user_email") || "").toLowerCase();
      const from = mail.from?.emailAddress?.address || "";
      const toAddrs = (mail.toRecipients || [])
        .map((r) => r?.emailAddress?.address || "")
        .filter(Boolean);
      const ccAddrs = (mail.ccRecipients || [])
        .map((r) => r?.emailAddress?.address || "")
        .filter(Boolean);
      const bccAddrs=(mail.bccRecipients || [])
         .map((r) => r?.emailAddress?.address || "")
        .filter(Boolean);
      // Reply to sender; put everyone else in CC excluding self
      const allCC = [...toAddrs, ...ccAddrs].filter(
        (e) => e.toLowerCase() !== me && e !== from
      );

      setShowCc(allCC.length > 0);
      setShowBcc(false);
      setComposeData({
        to: toAddrs,
        cc: ccAddrs,
        bcc: "",
        subject: `Re: PUG_CRM ${baseSubject}`,
        body: `
          <br/><br/>
          <hr/>
          <b>From:</b> ${mail.from?.emailAddress?.address || ""}<br/>
          <b>Sent:</b> ${new Date(mail.sentDateTime).toLocaleString()}<br/>
          <b>Subject:</b> ${mail.subject || ""}<br/>
          <br/>
          ${originalBody}
        `,
        attachments: [],
      });
    } else if (type === "forward") {
      setShowCc(false);
      setShowBcc(false);
      setComposeData({
        to: "",
        cc: "",
        bcc: "",
        subject: `Fwd: PUG_CRM ${baseSubject}`,
        body: `
          <br/><br/>
          <hr/>
          <b>---------- Forwarded message ----------</b><br/>
          <b>From:</b> ${mail.from?.emailAddress?.address || ""}<br/>
          <b>Sent:</b> ${new Date(mail.sentDateTime).toLocaleString()}<br/>
          <b>Subject:</b> ${mail.subject || ""}<br/>
          <br/>
          ${originalBody}
        `,
        attachments: [],
      });
    } else {
      resetForm();
    }
  }, [type, mail, originalMail, open]);

  if (!open) return null;

  /* ── helpers ── */
  const set = (key) => (e) =>
    setComposeData((d) => ({ ...d, [key]: e.target.value }));

  const onSelectFiles = (e) =>
    setComposeData((d) => ({
      ...d,
      attachments: [...(d.attachments || []), ...Array.from(e.target.files)],
    }));

  const removeFile = (idx) =>
    setComposeData((d) => ({
      ...d,
      attachments: d.attachments.filter((_, i) => i !== idx),
    }));

  const resetForm = () => {
    setComposeData(EMPTY_FORM);
    setErrors({});
    setShowCc(false);
    setShowBcc(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  /* ── Send ── */
  const onSend = async () => {
    setErrors({});

    // strip HTML tags for body validation
    const plainBody = composeData.body.replace(/<[^>]*>/g, "").trim();

    const result = composeSchema.safeParse({
      to: composeData.to,
      cc: composeData.cc || undefined,
      bcc: composeData.bcc || undefined,
      subject: composeData.subject,
      body: plainBody || composeData.body,
    });

    if (!result.success) {
      const fieldErrors = {};
      result.error.errors.forEach((err) => {
        fieldErrors[err.path[0]] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSending(true);
    try {
      const toList = composeData.to
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      const ccList = composeData.cc
        ? composeData.cc.split(",").map((e) => e.trim()).filter(Boolean)
        : [];
      const bccList = composeData.bcc
        ? composeData.bcc.split(",").map((e) => e.trim()).filter(Boolean)
        : [];

      const payload = {
        to: toList,
        cc: ccList,
        bcc: bccList,
        user_id:customer?.customer_details?.id,
        subject: composeData.subject,
        body: composeData.body,
        attachments: await Promise.all(
          (composeData.attachments || []).map(async (file) => ({
            "@odata.type": "#microsoft.graph.fileAttachment",
            name: file.name,
            contentType: file.type || "application/octet-stream",
            contentBytes: await toBase64(file),
          }))
        ),
      };

      const res = await PostRequest(
        `${baseURL}inbox/sendmail`,
        payload,
        true,
        { "Content-Type": "application/json" },
        {}
      );

      if (res?.status !== "success") throw new Error("Send failed");

      toast.success("Email sent successfully");
      resetForm();
      onClose();
      window.dispatchEvent(new Event("mail-sent"));
    } catch (err) {
      console.error("Send error:", err);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const title =
    type === "reply"
      ? "Reply"
      : type === "replyAll"
      ? "Reply All"
      : type === "forward"
      ? "Forward"
      : "New Message";

  /* ─────────────── RENDER ─────────────── */
  return (
    <>
      <style>{`
        .oc-panel {
          font-family: 'Segoe UI', system-ui, sans-serif;
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #fff;
        }
        .oc-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          border-bottom: 1px solid #e0e0e0;
          flex-shrink: 0;
          background: #fff;
        }
        .oc-header-title {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
        }
        .oc-close-btn {
          background: none;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 4px 14px;
          font-size: 13px;
          color: #444;
          cursor: pointer;
          transition: background .15s;
        }
        .oc-close-btn:hover { background: #f5f5f5; }

        .oc-fields {
          border-bottom: 1px solid #e0e0e0;
          flex-shrink: 0;
        }
        .oc-row {
          display: flex;
          align-items: center;
          padding: 0 20px;
          border-bottom: 1px solid #f0f0f0;
          min-height: 44px;
        }
        .oc-row:last-child { border-bottom: none; }
        .oc-label {
          font-size: 13px;
          color: #888;
          min-width: 58px;
          flex-shrink: 0;
          font-weight: 500;
        }
        .oc-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 14px;
          color: #1a1a1a;
          background: transparent;
          padding: 10px 6px;
        }
        .oc-input::placeholder { color: #bbb; }
        .oc-input.oc-input-error { border-bottom: 1px solid #d93025; }
        .oc-field-error {
          font-size: 11px;
          color: #d93025;
          padding: 0 20px 6px 78px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .oc-cc-bcc-btns {
          display: flex;
          gap: 4px;
          flex-shrink: 0;
        }
        .oc-cc-bcc-btn {
          background: none;
          border: none;
          font-size: 12px;
          color: #1a73e8;
          font-weight: 600;
          cursor: pointer;
          padding: 2px 8px;
          border-radius: 3px;
          letter-spacing: 0.2px;
        }
        .oc-cc-bcc-btn:hover { background: #e8f0fe; }
        .oc-remove-btn {
          background: none;
          border: none;
          color: #bbb;
          cursor: pointer;
          font-size: 14px;
          padding: 0 6px;
          line-height: 1;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background .12s, color .12s;
        }
        .oc-remove-btn:hover { color: #d93025; background: #fce8e6; }

        .oc-body {
          flex: 1;
          overflow-y: auto;
          padding: 12px 20px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .oc-body .ql-toolbar.ql-snow {
          border-color: #e0e0e0;
          border-radius: 4px 4px 0 0;
          background: #fafafa;
        }
        .oc-body .ql-container.ql-snow {
          border-color: #e0e0e0;
          border-radius: 0 0 4px 4px;
          min-height: 240px;
        }
        .oc-body .ql-editor { min-height: 240px; font-size: 14px; }
        .oc-body-error .ql-container.ql-snow { border-color: #d93025 !important; }

        /* Attachment chips */
        .oc-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
        .oc-chip {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #e8f0fe;
          border: 1px solid #c5d7f5;
          border-radius: 16px;
          padding: 4px 10px 4px 12px;
          font-size: 12px;
          color: #174ea6;
          max-width: 230px;
        }
        .oc-chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .oc-chip-size { color: #5f8ec7; font-size: 11px; flex-shrink: 0; }
        .oc-chip button {
          background: none;
          border: none;
          color: #8aabda;
          cursor: pointer;
          font-size: 13px;
          padding: 0;
          margin-left: 2px;
          flex-shrink: 0;
        }
        .oc-chip button:hover { color: #d93025; }

        /* Footer */
        .oc-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          border-top: 1px solid #e0e0e0;
          flex-shrink: 0;
          background: #fff;
        }
        .oc-footer-left { display: flex; align-items: center; gap: 10px; }
        .oc-footer-right { display: flex; align-items: center; }

        .oc-send-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #0078d4;
          color: #fff;
          border: none;
          border-radius: 4px;
          padding: 8px 22px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background .15s;
          letter-spacing: 0.2px;
        }
        .oc-send-btn:hover:not(:disabled) { background: #005fa3; }
        .oc-send-btn:disabled { background: #c5c5c5; cursor: not-allowed; }

        .oc-attach-btn {
          background: none;
          border: 1px solid #e0e0e0;
          color: #5f6368;
          cursor: pointer;
          border-radius: 6px;
          height: 36px;
          padding: 0 12px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          transition: background .12s, border-color .12s;
        }
        .oc-attach-btn:hover { background: #f1f3f4; border-color: #ccc; }

        .oc-discard-btn {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          font-size: 13px;
          padding: 6px 12px;
          border-radius: 4px;
          transition: color .12s, background .12s;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .oc-discard-btn:hover { color: #d93025; background: #fce8e6; }

        /* Backdrop */
        .oc-backdrop {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          z-index: 1055;
        }
        .oc-overlay {
          width: 100%; height: 100%;
          background: rgba(0,0,0,0.4);
        }
        .oc-panel-wrapper {
          position: absolute;
          top: 0; right: 0;
          height: 100%;
          width: 680px;
          max-width: 100vw;
          box-shadow: -4px 0 24px rgba(0,0,0,.18);
        }
      `}</style>

      {/* Backdrop */}
      <div className="oc-backdrop">
        <div className="oc-overlay" onClick={handleClose} />

        {/* Side panel */}
        <div
          className="oc-panel-wrapper oc-panel"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── HEADER ── */}
          <div className="oc-header">
            <span className="oc-header-title">{title}</span>
            <button className="oc-close-btn" onClick={handleClose}>
              ✕ Close
            </button>
          </div>

          {/* ── ADDRESS FIELDS ── */}
          <div className="oc-fields">
            {/* TO */}
            <div className="oc-row">
              <span className="oc-label">To</span>
              <input
                className={`oc-input${errors.to ? " oc-input-error" : ""}`}
                value={composeData.to}
                onChange={set("to")}
                placeholder="Recipients (comma-separated)"
              />
              <div className="oc-cc-bcc-btns">
                {!showCc && (
                  <button className="oc-cc-bcc-btn" onClick={() => setShowCc(true)}>
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button className="oc-cc-bcc-btn" onClick={() => setShowBcc(true)}>
                    Bcc
                  </button>
                )}
              </div>
            </div>
            {errors.to && (
              <div className="oc-field-error">⚠ {errors.to}</div>
            )}

            {/* CC */}
            {showCc && (
              <>
                <div className="oc-row">
                  <span className="oc-label">Cc</span>
                  <input
                    className={`oc-input${errors.cc ? " oc-input-error" : ""}`}
                    value={composeData.cc}
                    onChange={set("cc")}
                    placeholder="Cc recipients (comma-separated)"
                    autoFocus
                  />
                  <button
                    className="oc-remove-btn"
                    onClick={() => {
                      setShowCc(false);
                      setComposeData((d) => ({ ...d, cc: "" }));
                    }}
                  >
                    ✕
                  </button>
                </div>
                {errors.cc && (
                  <div className="oc-field-error">⚠ {errors.cc}</div>
                )}
              </>
            )}

            {/* BCC */}
            {showBcc && (
              <>
                <div className="oc-row">
                  <span className="oc-label">Bcc</span>
                  <input
                    className={`oc-input${errors.bcc ? " oc-input-error" : ""}`}
                    value={composeData.bcc}
                    onChange={set("bcc")}
                    placeholder="Bcc recipients (comma-separated)"
                    autoFocus
                  />
                  <button
                    className="oc-remove-btn"
                    onClick={() => {
                      setShowBcc(false);
                      setComposeData((d) => ({ ...d, bcc: "" }));
                    }}
                  >
                    ✕
                  </button>
                </div>
                {errors.bcc && (
                  <div className="oc-field-error">⚠ {errors.bcc}</div>
                )}
              </>
            )}

            {/* SUBJECT */}
            <div className="oc-row">
              <span className="oc-label">Subject</span>
              <input
                className={`oc-input${errors.subject ? " oc-input-error" : ""}`}
                value={composeData.subject}
                onChange={set("subject")}
                placeholder="Subject"
              />
            </div>
            {errors.subject && (
              <div className="oc-field-error">⚠ {errors.subject}</div>
            )}
          </div>

          {/* ── BODY ── */}
          <div className="oc-body">
            <div className={errors.body ? "oc-body-error" : ""}>
              <ReactQuill
                theme="snow"
                value={composeData.body}
                onChange={(v) => setComposeData((d) => ({ ...d, body: v }))}
                placeholder="Write your message..."
              />
            </div>
            {errors.body && (
              <div style={{ fontSize: 11, color: "#d93025", display: "flex", gap: 4 }}>
                ⚠ {errors.body}
              </div>
            )}

            {/* Attachment chips */}
            {composeData.attachments?.length > 0 && (
              <div className="oc-chips">
                {composeData.attachments.map((file, i) => (
                  <div key={i} className="oc-chip">
                    <span>📎</span>
                    <span className="oc-chip-name" title={file.name}>
                      {file.name}
                    </span>
                    <span className="oc-chip-size">
                      {(file.size / 1024).toFixed(0)}KB
                    </span>
                    <button onClick={() => removeFile(i)} title="Remove">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── FOOTER ── */}
          <div className="oc-footer">
            <div className="oc-footer-left">
              <button
                className="oc-send-btn"
                disabled={sending}
                onClick={onSend}
              >
                {sending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      style={{ width: 14, height: 14, borderWidth: 2 }}
                    />
                    Sending…
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3.5l16 6.5-16 6.5V12l11-2L2 8V3.5z" />
                    </svg>
                    Send
                  </>
                )}
              </button>

              <button
                className="oc-attach-btn"
                title="Attach files"
                onClick={() => fileInputRef.current?.click()}
              >
                📎 Attach
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={onSelectFiles}
              />
            </div>

            <div className="oc-footer-right">
              <button
                className="oc-discard-btn"
                onClick={handleClose}
              >
                🗑 Discard
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}